import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import XIcon from 'icons/X.icon';
import { Button, Loader } from 'ncoded-component-library';
import useScrollLock from 'hooks/useScrollLock';
import useCloseOnEscape from 'hooks/useCloseOnEscape';
import useScrollToStart from 'hooks/useScrollToStart';
import DrawerNavigationButtons from 'components/DrawerNavigationButtons';

import './SideDrawer.styles.scss';
import './SideDrawer.styles.responsive.scss';

export type DrawerNavigation = {
  onPrev?: () => void;
  onNext?: () => void;
};

export type SideDrawerProps = {
  className?: string;
  title?: React.ReactNode;
  isLoading?: boolean;
  children?: React.ReactNode;
  overlayClassName?: string;
  /**
   * Mount `children` on the first open instead of up-front (they stay mounted
   * afterwards). Only needed where many drawers are rendered at once — the
   * Files page renders four per row, and mounting them all is what makes large
   * folders freeze. Opt-in so drawers whose content has mount side effects keep
   * behaving as before.
   */
  lazyContent?: boolean;
  closeAction?: () => void;
  openAction?: () => void;
} & DrawerNavigation;

export type SideDrawerRef = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const SideDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  SideDrawerProps
> = (props, ref) => {
  const {
    children,
    className,
    title,
    isLoading,
    overlayClassName,
    lazyContent = false,
    closeAction,
    openAction,
    onPrev,
    onNext,
  } = props;
  const [open, setOpen] = useState(false);
  const firstRun = useRef(true);

  // Mount in the same render that opens the drawer, then keep it alive, so the
  // open drawer behaves exactly as it did before (fetching, form state, closing
  // animation).
  const wasOpened = useRef(false);
  if (open) wasOpened.current = true;

  const shouldRenderContent = !lazyContent || wasOpened.current;

  const [contentRef, scrollToStart] = useScrollToStart();

  const { lockScroll, unlockScroll } = useScrollLock();

  const classes = classNames(
    'side-drawer',
    { open, 'side-drawer--navigable': !!onPrev || !!onNext },
    className,
  );
  const overlayClasses = classNames(
    'side-drawer__overlay',
    { open },
    overlayClassName,
  );

  useImperativeHandle(
    ref,
    () => ({
      open,
      setOpen,
    }),
    [open],
  );

  const onClose = useCallback(() => setOpen(false), []);

  const handleOnPrev = useCallback(() => {
    onPrev?.();
    scrollToStart();
  }, [onPrev, scrollToStart]);

  const handleOnNext = useCallback(() => {
    onNext?.();
    scrollToStart();
  }, [onNext, scrollToStart]);

  useCloseOnEscape(onClose, { enabled: open });

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }

    if (open) {
      scrollToStart();
      lockScroll();
      openAction?.();
    } else {
      unlockScroll();
      closeAction?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => unlockScroll(), []);

  return (
    <>
      <div className={overlayClasses} onClick={onClose} />
      <div className={classes}>
        {title && <h2 className="side-drawer__title">{title}</h2>}
        <Button
          className="close-drawer-button"
          icon={XIcon}
          onClick={onClose}
        />
        {open && (
          <DrawerNavigationButtons
            {...(onPrev && { onPrev: handleOnPrev })}
            {...(onNext && { onNext: handleOnNext })}
          />
        )}

        <div className="side-drawer__content" ref={contentRef}>
          {isLoading ? <Loader /> : shouldRenderContent && children}
        </div>
      </div>
    </>
  );
};

export default forwardRef(SideDrawer);
